import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readNetworkIdsFromParam } from '@/utils/routeUtils';
import { aggregateShows } from '@/utils';
import type { Network, SelectedNetwork } from '@/types';
import type { DerivedNetworkContextType } from '@/context';

const EMPTY_SELECTED_NETWORK: SelectedNetwork = {
  id: undefined,
  name: undefined,
  shows: [],
  accessLevel: 'network',
};

/**
 * Derives the selected networks from the `?networks=` query param (comma-separated network ids) for
 * the multi-network content performance route.
 *
 * Ids are parsed, deduped, and capped at {@link MAX_NETWORK_IDS} in URL order. Unknown ids (not in
 * the user's authorized list) are dropped naturally at the lookup step rather than pre-filtered,
 * so the URL param is not coupled to the auth list. The primary network is the first resolved entry.
 * @param {Network[] | undefined} networks - The user's authorized networks for the current app type.
 * @param {boolean} isPending - Whether the networks are still loading.
 * @returns {DerivedNetworkContextType} The primary network and the full list of selected networks.
 */
const useDerivedNetworks = (
  networks: Network[] | undefined,
  isPending: boolean
): DerivedNetworkContextType => {
  const [searchParams] = useSearchParams();
  const networksParam = searchParams.get('networks');

  return useMemo(() => {
    const resolvedIds = isPending ? [] : readNetworkIdsFromParam(networksParam);

    if (resolvedIds.length === 0 || !networks) {
      return { selectedNetwork: EMPTY_SELECTED_NETWORK, selectedNetworks: [], shows: [] };
    }

    const networkById = new Map(networks.map((network) => [network.id, network]));
    const selectedNetworks: SelectedNetwork[] = resolvedIds
      .map((resolvedId) => networkById.get(resolvedId))
      .filter((network): network is Network => network !== undefined)
      .map(({ id, name, shows, accessLevel }) => ({ id, name, shows, accessLevel }));

    // All ids in the URL were unknown — none resolved to network objects.
    if (selectedNetworks.length === 0) {
      return { selectedNetwork: EMPTY_SELECTED_NETWORK, selectedNetworks: [], shows: [] };
    }

    return { selectedNetwork: selectedNetworks[0], selectedNetworks, shows: aggregateShows(selectedNetworks) };
  }, [isPending, networks, networksParam]);
};

export default useDerivedNetworks;
